// Region templates: named polygons in a 1800×900 panel, keyed archetype/metaphor.
// Coordinates are fractions of panel width/height so panels can resize.
// `role` is the semantic job the region does for the scribe; `flow` is the
// packing order preference inside the region; `terrain` is what a box placed
// there sits in (board/terrain.js dresses the text box to match). `anchor`
// is the metaphor art (symbol_id, centre, height, opacity) or null for a page
// without one — every reader guards for null.

export const PANEL = { w: 1800, h: 900 };

const R = (name, role, pts, opts = {}) => ({ name, role, pts, flow: 'center', ...opts });

export const TEMPLATES = {
  // The opening sketch's page (TT-1133): no metaphor yet, so no anchor art —
  // a band of sky for a few thought-cloud text boxes, plain paper below.
  // Sketch-only: the planner (platform domain/scenegen/board_templates.py)
  // never emits it, so it needs no Python twin.
  'notes/plain': {
    anchor: null,
    regions: [
      R('top',    'notes', [[0.02,0.02],[0.98,0.02],[0.98,0.40],[0.02,0.40]], { flow: 'left', terrain: 'sky' }),
      R('middle', 'notes', [[0.02,0.42],[0.98,0.42],[0.98,0.70],[0.02,0.70]], { flow: 'left' }),
      R('bottom', 'notes', [[0.02,0.72],[0.98,0.72],[0.98,0.98],[0.02,0.98]], { flow: 'left' }),
    ],
  },
  'landscape/mountain': {
    anchor: { symbol_id: 'mountain', x: 0.5, y: 0.56, h: 0.95, opacity: 0.42 },
    regions: [
      R('sky',    'vision',     [[0.02,0.02],[0.98,0.02],[0.98,0.34],[0.66,0.30],[0.50,0.16],[0.34,0.30],[0.02,0.34]], { flow: 'left', terrain: 'sky' }),
      R('summit', 'goal',       [[0.36,0.18],[0.64,0.18],[0.68,0.54],[0.32,0.54]]),
      R('slope',  'journey',    [[0.02,0.38],[0.34,0.40],[0.30,0.72],[0.02,0.72]], { flow: 'top', terrain: 'ground' }),
      R('slope-r','journey',    [[0.66,0.40],[0.98,0.38],[0.98,0.72],[0.70,0.72]], { flow: 'top', terrain: 'ground' }),
      R('ground', 'foundation', [[0.02,0.76],[0.98,0.76],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'landscape/tree': {
    anchor: { symbol_id: 'tree', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('canopy-l','outcomes',  [[0.02,0.02],[0.36,0.02],[0.36,0.50],[0.02,0.50]], { terrain: 'canopy' }),
      R('canopy-r','outcomes',  [[0.64,0.02],[0.98,0.02],[0.98,0.50],[0.64,0.50]], { terrain: 'canopy' }),
      R('crown',   'goal',      [[0.36,0.02],[0.64,0.02],[0.64,0.24],[0.36,0.24]], { terrain: 'canopy' }),
      R('trunk',   'strategy',  [[0.30,0.52],[0.70,0.52],[0.70,0.70],[0.30,0.70]]),
      R('roots',   'values',    [[0.02,0.66],[0.98,0.66],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'underground' }),
    ],
  },
  'poster/iceberg': {
    anchor: { symbol_id: 'iceberg', x: 0.5, y: 0.52, h: 0.98, opacity: 0.42 },
    regions: [
      R('above',  'visible',    [[0.02,0.02],[0.98,0.02],[0.98,0.36],[0.02,0.36]], { terrain: 'sky' }),
      R('below-l','hidden',     [[0.02,0.42],[0.40,0.42],[0.40,0.98],[0.02,0.98]], { flow: 'top', terrain: 'water' }),
      R('below-r','hidden',     [[0.60,0.42],[0.98,0.42],[0.98,0.98],[0.60,0.98]], { flow: 'top', terrain: 'water' }),
    ],
  },
  // TT-1219: the river's own art — a spring at top left, one S-bend through
  // the middle, a delta at bottom right; the regions follow the water.
  'path/river': {
    anchor: { symbol_id: 'river', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('source',    'origin',     [[0.02,0.02],[0.34,0.02],[0.34,0.48],[0.02,0.48]], { flow: 'top', terrain: 'water' }),
      R('bend',      'milestones', [[0.38,0.04],[0.62,0.04],[0.62,0.96],[0.38,0.96]], { flow: 'top', terrain: 'water' }),
      R('mouth',     'outcome',    [[0.66,0.52],[0.98,0.52],[0.98,0.98],[0.66,0.98]], { flow: 'top', terrain: 'water' }),
      R('banks',     'notes',      [[0.02,0.52],[0.34,0.52],[0.34,0.98],[0.02,0.98]], { flow: 'top', terrain: 'ground' }),
      R('far-bank',  'notes',      [[0.66,0.02],[0.98,0.02],[0.98,0.48],[0.66,0.48]], { flow: 'top', terrain: 'ground' }),
    ],
  },
  'mandala/spiral': {
    anchor: { symbol_id: 'mandala', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('center', 'purpose',   [[0.38,0.34],[0.62,0.34],[0.62,0.66],[0.38,0.66]]),
      R('ring-n', 'discussed', [[0.30,0.02],[0.70,0.02],[0.70,0.30],[0.30,0.30]]),
      R('ring-e', 'agreed',    [[0.66,0.30],[0.98,0.30],[0.98,0.70],[0.66,0.70]]),
      R('ring-s', 'differed',  [[0.30,0.70],[0.70,0.70],[0.70,0.98],[0.30,0.98]]),
      R('ring-w', 'together',  [[0.02,0.30],[0.34,0.30],[0.34,0.70],[0.02,0.70]]),
    ],
  },
  'cluster/cloud': {
    anchor: { symbol_id: 'cloud', x: 0.5, y: 0.5, h: 0.9, opacity: 0.42 },
    regions: [
      R('blob-a', 'ideas', [[0.02,0.02],[0.48,0.02],[0.48,0.48],[0.02,0.48]], { flow: 'top', terrain: 'sky' }),
      R('blob-b', 'ideas', [[0.52,0.02],[0.98,0.02],[0.98,0.48],[0.52,0.48]], { flow: 'top', terrain: 'sky' }),
      R('blob-c', 'ideas', [[0.02,0.52],[0.48,0.52],[0.48,0.98],[0.02,0.98]], { flow: 'top', terrain: 'sky' }),
      R('blob-d', 'ideas', [[0.52,0.52],[0.98,0.52],[0.98,0.98],[0.52,0.98]], { flow: 'top', terrain: 'sky' }),
    ],
  },
  'landscape/sun': {
    anchor: { symbol_id: 'sun', x: 0.5, y: 0.42, h: 0.9, opacity: 0.42 },
    regions: [
      R('sky',     'purpose', [[0.02,0.02],[0.98,0.02],[0.98,0.40],[0.02,0.40]], { terrain: 'sky' }),
      R('horizon', 'energy',  [[0.02,0.44],[0.98,0.44],[0.98,0.70],[0.02,0.70]], { flow: 'left' }),
      R('ground',  'nourish', [[0.02,0.74],[0.98,0.74],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'grid/scale': {
    anchor: { symbol_id: 'x', x: 0.5, y: 0.5, h: 0.6, opacity: 0.18 },
    regions: [
      R('title-row', 'crossing', [[0.02,0.02],[0.98,0.02],[0.98,0.22],[0.02,0.22]]),
      R('left',      'road',     [[0.02,0.26],[0.48,0.26],[0.48,0.98],[0.02,0.98]], { flow: 'top' }),
      R('right',     'road',     [[0.52,0.26],[0.98,0.26],[0.98,0.98],[0.52,0.98]], { flow: 'top' }),
    ],
  },

  // TT-1219: a page for every scene backdrop the board lacked. The art is a
  // centred square (h × panel height), so its parts sit in x ≈ 0.26–0.74;
  // each template puts the picture's own parts to work — the volcano's
  // crater, the hand's palm, the eye's iris — and lays the wider panel out
  // on either side.
  'landscape/volcano': {
    anchor: { symbol_id: 'volcano', x: 0.5, y: 0.54, h: 0.95, opacity: 0.42 },
    regions: [
      R('plume',   'release',    [[0.02,0.02],[0.98,0.02],[0.98,0.28],[0.02,0.28]], { flow: 'left', terrain: 'sky' }),
      R('crater',  'flashpoint', [[0.38,0.32],[0.62,0.32],[0.64,0.54],[0.36,0.54]]),
      R('slope-l', 'pressure',   [[0.02,0.32],[0.34,0.32],[0.30,0.74],[0.02,0.74]], { flow: 'top', terrain: 'ground' }),
      R('slope-r', 'response',   [[0.66,0.32],[0.98,0.32],[0.98,0.74],[0.70,0.74]], { flow: 'top', terrain: 'ground' }),
      R('chamber', 'stakes',     [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'underground' }),
    ],
  },
  'network/web': {
    anchor: { symbol_id: 'web', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('hub',      'core',     [[0.38,0.36],[0.62,0.36],[0.62,0.64],[0.38,0.64]]),
      R('strand-n', 'partners', [[0.02,0.02],[0.98,0.02],[0.98,0.32],[0.02,0.32]], { flow: 'left' }),
      R('strand-w', 'ties',     [[0.02,0.36],[0.34,0.36],[0.34,0.98],[0.02,0.98]], { flow: 'top' }),
      R('strand-e', 'ties',     [[0.66,0.36],[0.98,0.36],[0.98,0.98],[0.66,0.98]], { flow: 'top' }),
      R('strand-s', 'gaps',     [[0.38,0.68],[0.62,0.68],[0.62,0.98],[0.38,0.98]]),
    ],
  },
  'loop/spiral': {
    anchor: { symbol_id: 'spiral', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('center', 'origin', [[0.40,0.38],[0.60,0.38],[0.60,0.62],[0.40,0.62]]),
      R('turn-1', 'round',  [[0.02,0.30],[0.36,0.30],[0.36,0.98],[0.02,0.98]], { flow: 'top' }),
      R('turn-2', 'round',  [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left' }),
      R('turn-3', 'round',  [[0.64,0.30],[0.98,0.30],[0.98,0.98],[0.64,0.98]], { flow: 'top' }),
      R('next',   'next',   [[0.40,0.68],[0.60,0.68],[0.60,0.98],[0.40,0.98]]),
    ],
  },
  'strata/layers': {
    anchor: { symbol_id: 'layers-of-earth', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('surface', 'today',      [[0.02,0.02],[0.98,0.02],[0.98,0.24],[0.02,0.24]], { flow: 'left', terrain: 'ground' }),
      R('topsoil', 'recent',     [[0.02,0.28],[0.98,0.28],[0.98,0.48],[0.02,0.48]], { flow: 'left', terrain: 'underground' }),
      R('clay',    'groundwork', [[0.02,0.52],[0.98,0.52],[0.98,0.72],[0.02,0.72]], { flow: 'left', terrain: 'underground' }),
      R('bedrock', 'bedrock',    [[0.02,0.76],[0.98,0.76],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'underground' }),
    ],
  },
  'figure/hand': {
    anchor: { symbol_id: 'hand', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('fingertips', 'delivered', [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left' }),
      R('palm',       'work',      [[0.38,0.40],[0.62,0.40],[0.62,0.70],[0.38,0.70]]),
      R('left',       'plans',     [[0.02,0.30],[0.34,0.30],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('right',      'owners',    [[0.66,0.30],[0.98,0.30],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('wrist',      'capacity',  [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'force/wind': {
    anchor: { symbol_id: 'wind', x: 0.5, y: 0.5, h: 0.95, opacity: 0.42 },
    regions: [
      R('gusts',    'forces',    [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left', terrain: 'sky' }),
      R('headwind', 'against',   [[0.02,0.30],[0.36,0.30],[0.36,0.74],[0.02,0.74]], { flow: 'top' }),
      R('course',   'direction', [[0.40,0.32],[0.60,0.32],[0.60,0.72],[0.40,0.72]]),
      R('tailwind', 'with',      [[0.64,0.30],[0.98,0.30],[0.98,0.74],[0.64,0.74]], { flow: 'top' }),
      R('trim',     'adapt',     [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'force/wave': {
    anchor: { symbol_id: 'wave', x: 0.5, y: 0.52, h: 0.95, opacity: 0.42 },
    regions: [
      R('crest',    'shift',   [[0.02,0.02],[0.98,0.02],[0.98,0.28],[0.02,0.28]], { flow: 'left', terrain: 'sky' }),
      R('swell',    'build',   [[0.02,0.32],[0.34,0.32],[0.34,0.98],[0.02,0.98]], { flow: 'top', terrain: 'water' }),
      R('ride',     'moves',   [[0.38,0.34],[0.62,0.34],[0.62,0.66],[0.38,0.66]]),
      R('break',    'landing', [[0.66,0.32],[0.98,0.32],[0.98,0.98],[0.66,0.98]], { flow: 'top', terrain: 'ground' }),
      R('undertow', 'risk',    [[0.38,0.70],[0.62,0.70],[0.62,0.98],[0.38,0.98]], { terrain: 'water' }),
    ],
  },
  'force/flames': {
    anchor: { symbol_id: 'flames', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('sparks', 'new',        [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left' }),
      R('heart',  'change',     [[0.38,0.32],[0.62,0.32],[0.62,0.64],[0.38,0.64]]),
      R('fuel',   'letting-go', [[0.02,0.30],[0.34,0.30],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('smoke',  'cost',       [[0.66,0.30],[0.98,0.30],[0.98,0.74],[0.66,0.74]], { flow: 'top', terrain: 'sky' }),
      R('ash',    'remains',    [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'sky/lightning': {
    anchor: { symbol_id: 'sky-lightning', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('storm',    'buildup',  [[0.02,0.02],[0.98,0.02],[0.98,0.28],[0.02,0.28]], { flow: 'left', terrain: 'sky' }),
      R('before',   'before',   [[0.02,0.32],[0.34,0.32],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('bolt',     'strike',   [[0.38,0.34],[0.62,0.34],[0.62,0.66],[0.38,0.66]]),
      R('after',    'after',    [[0.66,0.32],[0.98,0.32],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('grounded', 'response', [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'sky/moon': {
    anchor: { symbol_id: 'moon', x: 0.5, y: 0.5, h: 0.95, opacity: 0.42 },
    regions: [
      R('new',    'dark',      [[0.02,0.02],[0.34,0.02],[0.34,0.48],[0.02,0.48]], { flow: 'top', terrain: 'sky' }),
      R('waxing', 'surfacing', [[0.66,0.02],[0.98,0.02],[0.98,0.48],[0.66,0.48]], { flow: 'top', terrain: 'sky' }),
      R('full',   'light',     [[0.66,0.52],[0.98,0.52],[0.98,0.98],[0.66,0.98]], { flow: 'top', terrain: 'sky' }),
      R('waning', 'quiet',     [[0.02,0.52],[0.34,0.52],[0.34,0.98],[0.02,0.98]], { flow: 'top', terrain: 'sky' }),
      R('rest',   'rest',      [[0.38,0.34],[0.62,0.34],[0.62,0.66],[0.38,0.66]]),
    ],
  },
  'sky/star': {
    anchor: { symbol_id: 'star', x: 0.5, y: 0.5, h: 0.95, opacity: 0.42 },
    regions: [
      R('glow',  'values', [[0.02,0.02],[0.98,0.02],[0.98,0.28],[0.02,0.28]], { flow: 'left', terrain: 'sky' }),
      R('star',  'aim',    [[0.38,0.32],[0.62,0.32],[0.62,0.74],[0.38,0.74]]),
      R('today', 'today',  [[0.02,0.32],[0.34,0.32],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('path',  'path',   [[0.66,0.32],[0.98,0.32],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('proof', 'wins',   [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left' }),
    ],
  },
  'figure/eye': {
    anchor: { symbol_id: 'eye', x: 0.5, y: 0.5, h: 0.95, opacity: 0.42 },
    regions: [
      R('brow',  'focus',     [[0.02,0.02],[0.98,0.02],[0.98,0.30],[0.02,0.30]], { flow: 'left' }),
      R('seen',  'observed',  [[0.02,0.34],[0.34,0.34],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('iris',  'insight',   [[0.40,0.36],[0.60,0.36],[0.60,0.64],[0.40,0.64]]),
      R('blind', 'blindspot', [[0.66,0.34],[0.98,0.34],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('lens',  'method',    [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left' }),
    ],
  },
  'cycle/triskelion': {
    anchor: { symbol_id: 'triskelion', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('heart',   'heart',      [[0.40,0.36],[0.60,0.36],[0.60,0.64],[0.40,0.64]]),
      R('arm-top', 'giving',     [[0.30,0.02],[0.70,0.02],[0.70,0.32],[0.30,0.32]]),
      R('arm-l',   'receiving',  [[0.02,0.30],[0.36,0.30],[0.36,0.98],[0.02,0.98]], { flow: 'top' }),
      R('arm-r',   'sustaining', [[0.64,0.30],[0.98,0.30],[0.98,0.98],[0.64,0.98]], { flow: 'top' }),
      R('thanks',  'thanks',     [[0.40,0.68],[0.60,0.68],[0.60,0.98],[0.40,0.98]]),
    ],
  },
  'figure/elephant': {
    anchor: { symbol_id: 'elephant', x: 0.5, y: 0.52, h: 0.95, opacity: 0.42 },
    regions: [
      R('room',     'said',    [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left' }),
      R('trunk',    'signs',   [[0.02,0.30],[0.34,0.30],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('elephant', 'unsaid',  [[0.38,0.32],[0.62,0.32],[0.62,0.62],[0.38,0.62]]),
      R('tail',     'power',   [[0.66,0.30],[0.98,0.30],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('floor',    'naming',  [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'figure/brain': {
    anchor: { symbol_id: 'profile-with-brain', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('crown',  'ideas',     [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left' }),
      R('inputs', 'learning',  [[0.02,0.30],[0.34,0.30],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('mind',   'mindset',   [[0.38,0.30],[0.58,0.30],[0.58,0.58],[0.38,0.58]]),
      R('voice',  'applying',  [[0.66,0.30],[0.98,0.30],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('ground', 'wellbeing', [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left' }),
    ],
  },
  'figure/snake': {
    anchor: { symbol_id: 'snake', x: 0.5, y: 0.5, h: 1.0, opacity: 0.42 },
    regions: [
      R('head',  'question', [[0.30,0.02],[0.70,0.02],[0.70,0.28],[0.30,0.28]]),
      R('one',   'for',      [[0.02,0.02],[0.26,0.02],[0.26,0.98],[0.02,0.98]], { flow: 'top' }),
      R('coil',  'tangle',   [[0.36,0.34],[0.64,0.34],[0.64,0.66],[0.36,0.66]]),
      R('other', 'against',  [[0.74,0.02],[0.98,0.02],[0.98,0.98],[0.74,0.98]], { flow: 'top' }),
      R('tail',  'decision', [[0.30,0.72],[0.70,0.72],[0.70,0.98],[0.30,0.98]]),
    ],
  },
  'figure/turtle': {
    anchor: { symbol_id: 'turtle', x: 0.5, y: 0.5, h: 0.95, opacity: 0.42 },
    regions: [
      R('weather', 'difficulty', [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left', terrain: 'sky' }),
      R('behind',  'covered',    [[0.02,0.30],[0.34,0.30],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('shell',   'resilience', [[0.38,0.34],[0.62,0.34],[0.62,0.64],[0.38,0.64]]),
      R('ahead',   'goal',       [[0.66,0.30],[0.98,0.30],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('steps',   'steps',      [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'shelter/dome': {
    anchor: { symbol_id: 'buckyball', x: 0.5, y: 0.5, h: 0.95, opacity: 0.42 },
    regions: [
      R('frame',   'structure',  [[0.02,0.02],[0.98,0.02],[0.98,0.26],[0.02,0.26]], { flow: 'left' }),
      R('outside', 'threats',    [[0.02,0.30],[0.34,0.30],[0.34,0.74],[0.02,0.74]], { flow: 'top' }),
      R('inside',  'protected',  [[0.38,0.34],[0.62,0.34],[0.62,0.64],[0.38,0.64]]),
      R('door',    'welcome',    [[0.66,0.30],[0.98,0.30],[0.98,0.74],[0.66,0.74]], { flow: 'top' }),
      R('footing', 'foundation', [[0.02,0.78],[0.98,0.78],[0.98,0.98],[0.02,0.98]], { flow: 'left', terrain: 'ground' }),
    ],
  },
  'sky/bird': {
    anchor: { symbol_id: 'bird', x: 0.5, y: 0.5, h: 0.95, opacity: 0.42 },
    regions: [
      R('horizon', 'future',  [[0.02,0.02],[0.98,0.02],[0.98,0.28],[0.02,0.28]], { flow: 'left', terrain: 'sky' }),
      R('wings',   'lift',    [[0.38,0.32],[0.62,0.32],[0.62,0.62],[0.38,0.62]]),
      R('flight',  'path',    [[0.66,0.32],[0.98,0.32],[0.98,0.98],[0.66,0.98]], { flow: 'top', terrain: 'sky' }),
      R('nest',    'release', [[0.02,0.32],[0.34,0.32],[0.34,0.98],[0.02,0.98]], { flow: 'top', terrain: 'canopy' }),
    ],
  },
};

export function getTemplate(archetype, metaphor) {
  const key = `${archetype}/${metaphor}`;
  const t = TEMPLATES[key];
  if (!t) throw new Error(`no template for ${key}`);
  return t;
}

export function regionPolygon(region, panel = PANEL) {
  return region.pts.map(([fx, fy]) => [fx * panel.w, fy * panel.h]);
}
